/* eslint-disable @typescript-eslint/no-use-before-define, @typescript-eslint/camelcase */
/* global angular */
import _ from 'lodash'
import intl from 'react-intl-universal'
import { sanitize } from 'dompurify'

import toolboxDefaults from './toolbox'
import moment from 'moment-timezone'
import { getKey, isCustomEmbedByKey, getSubtype } from '~/util/customEmbed'
import CONSTANTS from '~/constants'
import { VARIANTS_ZONE_KEY, EVIDENTLY_FLAG } from '~/util/constants/keys'
import _labelsModel from './model/labels.js'
import {
  deleteCustomEmbed,
  setSettings,
  deleteAnnotation,
} from '~/redux/settingsReducer'

// Build Timezone Array for autocomplete
const TIMEZONES = moment.tz.names().map((name) => {
  const unfilteredZones = moment.tz.zone(name).abbrs
  const abbrs = unfilteredZones.filter(
    (el, index) => unfilteredZones.indexOf(el) === index
  )
  return {
    label: `${name.replace('_', ' ')} ( ${abbrs} )`,
    value: name,
  }
})

const returnOnlyChecked = (arr) =>
  arr.filter((i) => i.selected && i.property).map((i) => i.property.key)

const mapKeySettings = (o) => ({ key: o.key, settings: o.settings || {} })

export const MIN_IDLE_TIMEOUT = 10
export const MIN_IDLE_WARNING = 5

export default [
  'searchFiltersService',
  '$scope',
  '$interval',
  'metaTemplatesService',
  'settingsService',
  'toastr',
  '$rootScope',
  '$location',
  'storyTemplateService',
  'siteService',
  '$ngRedux',
  'evidentlyService',
  SettingsController,
]

function SettingsController(
  searchFiltersService,
  $scope,
  $interval,
  metaTemplatesService,
  settingsService,
  toastr,
  $rootScope,
  $location,
  storyTemplateService,
  siteService,
  $ngRedux,
  evidentlyService
) {
  const metaItems = metaTemplatesService.getMetaItems()

  $rootScope.requiresActions = false
  $scope.status = 'loading'
  $scope.companySettings = {}
  $scope.planning_options = []
  $scope.toolbox_options = []
  $scope.meta_options = []
  $scope.ans_overrides = []
  $scope.tags_defaults = []
  $scope.meta_options_remaining = []
  $scope.piklists = {}
  $scope.edit = {}
  $scope.ninja = !!window.localStorage.ninja
  $scope.timeZoneLabel = ''
  $scope.webSites = null
  $scope.intl = intl
  $scope.annotations_enable = false
  $scope.shouldRenderOptions = false
  $scope.toolboxDefaults = _.cloneDeep(toolboxDefaults)
  $scope.preventEnterInSettingsModal = (event) => {
    // https://arcpublishing.atlassian.net/browse/COMP-7579
    if (event.key === 'Enter') {
      event.preventDefault()
      $('#settingsModal').modal('hide')
    }
  }

  siteService.getTopLevelSites().then((sites) => {
    sites.unshift({ id: 'default', name: '* Default *' })
    $scope.topLevelSites = sites
  })

  siteService
    .getWebsites()
    .then((webSites) => {
      $scope.webSites = webSites
    })
    .catch((error) => {
      $scope.webSites = { error }
    })

  this.$onInit = async () => {
    $scope.getTimeZoneLabel()
    storyTemplateService.list()
    /**
     * We need to move this to toolbox.js once
     * the feature flag in removed in the future
     * and enabled for everyone
     */
    $scope.ifx_send_to_print_url = CONSTANTS.TRIDENT_ROUTE_IFX_INVOKE_API
    $scope.ifx_send_to_print_enabled = true
  }

  const fetchEvidentlyFeatures = async () => {
    const evidentlyFeatures = await evidentlyService.instance()

    if (evidentlyFeatures[EVIDENTLY_FLAG.CONDITIONAL_CONTENT]) {
      $scope.toolboxDefaults.push({
        key: VARIANTS_ZONE_KEY,
        text: intl.get('settings.toolboxOptions.variant_zone'),
        default: true,
      })
    }
    if (evidentlyFeatures[EVIDENTLY_FLAG.ANNOTATIONS]) {
      $scope.annotations_enable = true
    }
    if (evidentlyFeatures[EVIDENTLY_FLAG.AUDIO_CENTER_INTEGRATION]) {
      $scope.toolboxDefaults.push({
        key: 'audio',
        text: intl.get('settings.toolboxOptions.audio'),
        default: false,
      })
    }
    if (evidentlyFeatures[EVIDENTLY_FLAG.CONTENT_TYPE]) {
      $scope.toolboxDefaults.push({
        key: 'content_type',
        text: intl.get('settings.toolboxOptions.content_type'),
        default: false,
      })
    }
  }

  // Timezones list label
  // NOTE: Mike: tried using ngtagsinput but it sucks
  // Get label for input
  $scope.getTimeZoneLabel = function () {
    const orgTimeZone = TIMEZONES.find(
      (i) => i.value === $scope.companySettings.timezone
    )

    if (orgTimeZone) {
      $scope.timeZoneLabel = orgTimeZone.label
      $scope.companySettings.timezone = orgTimeZone.value
    } else {
      $scope.companySettings.timezone = $scope.timeZoneLabel = moment.tz.guess()
    }
  }

  // Delete a Custom Template confirmed from modal click
  $rootScope.$on('SETTINGS_CONFIRM_DELETE_TEMPLATE', (sender, { id }) => {
    storyTemplateService.delete(id).then((templates) => {
      $scope.companySettings = {
        ...$scope.companySettings,
        templates,
      }
    })
  })

  // init delete custom template, calls modal
  $scope.deleteTemplate = (id) => {
    $rootScope.$broadcast('MODAL', {
      type: 'DELETE_TEMPLATE',
      payload: {
        id,
        confirmTemplateDelete: () => {
          $rootScope.$broadcast('SETTINGS_CONFIRM_DELETE_TEMPLATE', { id })
          $rootScope.$broadcast('MODAL', { type: 'CLOSE' })
        },
      },
    })
  }
  // Edit a Custom Template
  $scope.editTemplate = (id) => {
    $location.path('template').search({ template: id })
  }
  // Create a Custom Template
  $scope.createTemplate = () => {
    $location.path('template').search({ template: 'default' })
  }
  // Set Default Template
  $scope.setDefaultTemplate = (id) => {
    storyTemplateService.set(id).then((data) => {
      $scope.companySettings.templates = data
    })
  }
  // toggle Template visibility
  $scope.setvisibilityTemplate = (template) => {
    const templates = $scope.companySettings.templates
    // Toggle
    template.visible = !template.visible
    // check
    if (templates.every((i) => i.visible === false)) {
      template.visible = true
      toastr.warning(intl.get('settings.setvisibilityTemplate.warning'))
      // set default visible
      // let index = templates.findIndex(i => i.id === 'default')
      // templates[index].visible = true
    }
  }

  this.updateSettingOptions = (orgSettings) => {
    // todo: daniel: remove filter later, will let users
    // configure all filters
    return searchFiltersService.getFilters({}).then((filters) => {
      const searchFilters = filters.leftFilters
        .filter((obj) => {
          return (
            obj.label ===
            intl.get('searchFiltersService.leftFilters.sourceLabel')
          )
        })
        // make it a hash for convenience in searchFilter service
        .reduce((acc, obj) => {
          const orgDefault = (orgSettings.searchFilters || {})[obj.label] || ''
          return {
            ...acc,
            [obj.label]: orgDefault || '',
          }
        }, {})

      $scope.companySettings = Object.assign({}, orgSettings, { searchFilters })

      fetchEvidentlyFeatures().then(() => {
        checkAndSort(
          $scope.companySettings.tools,
          $scope.companySettings.customEmbed,
          $scope.toolboxDefaults,
          'toolbox_options'
        )
      })

      $scope.metasFields = $scope.companySettings.v2.metas.map((k) => {
        const o = metaItems[k.key] || {}
        o.settings = k.settings
        o.key = k.key
        return o
      })

      // https://arcpublishing.atlassian.net/browse/EL-1073
      $scope.companySettings.v2.planning = _.filter(
        $scope.companySettings.v2.planning,
        function (entry) {
          if (Object.prototype.hasOwnProperty.call(metaItems, entry.key)) {
            return Object.prototype.hasOwnProperty.call(
              metaItems[entry.key],
              'label'
            )
          }
          return false
        }
      )
      $scope.planningFields = $scope.companySettings.v2.planning.map((k) => {
        const o = metaItems[k.key] || {}
        o.settings = k.settings
        o.key = k.key
        return o
      })

      // Timezone labels
      $scope.getTimeZoneLabel()

      updateAvailableMetas(false)
      $scope.renderOpenBetaToggleElement()
      $scope.renderAutomationNotificationElement()
      $scope.renderHyperlinkConfigElement()
      return filters
    })
  }

  $scope.addSiteServiceBasePaths = () => {
    $scope.companySettings.siteServiceBasePaths.push({ id: '', path: '' })
  }
  $scope.removeSiteServiceBasePaths = (index) => {
    $scope.companySettings.siteServiceBasePaths.splice(index, 1)
  }

  $scope.handleOpenBetaSettings = ({
    enabled,
    byDefault,
    legacyEnabled,
    isInlineCommentsEnabled,
  }) => {
    $scope.companySettings.openBeta = {
      enabled,
      default: byDefault,
      legacyEnabled,
      isInlineCommentsEnabled,
    }
  }

  $scope.handleAutomationNotificationSettings = (settings) => {
    $scope.companySettings.dss = {
      notifications: {
        ...$scope.companySettings.dss?.notifications,
        ...settings,
      },
    }
  }

  $scope.handleHyperlinkConfigSettings = (settings) => {
    $scope.companySettings.hyperlink = settings
  }

  function updateAvailableMetas(useCurrent) {
    const metas = useCurrent
      ? $scope.metasFields
      : $scope.companySettings.v2.metas
    const planning = useCurrent
      ? $scope.planningFields
      : $scope.companySettings.v2.planning
    const usedKeys = metas.map((o) => o.key).concat(planning.map((o) => o.key))
    const excludeMetaElements = ['section', 'canonical_website']

    // $scope.meta_options_remaining = meta_defaults
    //                                    .filter( o => usedKeys.indexOf(o.key) === -1)
    //                                    .sort((a,b) => a.text > b.text ? 1 : -1 )

    $scope.meta_options_remaining = Object.keys(metaItems)
      .reduce((arr, key) => {
        if (!metaItems[key].key) {
          metaItems[key].key = key
        }
        if (usedKeys.indexOf(key) === -1 && key !== 'unknown') {
          arr.push(metaItems[key])
        }
        return arr
      }, [])
      .filter((item) => {
        if (excludeMetaElements.some((meta) => meta === item.key)) {
          return false
        }
        return true
      })
      .sort((a, b) => (a.label > b.label ? 1 : -1))
  }

  function checkAndSort(compSettingArr, customEmbed = {}, defaults, outputKey) {
    // old version was not an array, but an object, so coverting over
    if (!$.isArray(compSettingArr)) {
      compSettingArr = Object.keys(compSettingArr)
    }
    // For quick look up, create oject with key reference
    const hashItems = defaults.reduce((o, itm) => {
      o[itm.key] = itm
      return o
    }, {})

    // Get the options that are already selected by the company
    let formOptions = []
    if (compSettingArr) {
      formOptions = compSettingArr
        .map(function (key, index) {
          let property
          if (isCustomEmbedByKey(key)) {
            const subtype = getSubtype(key)
            if (customEmbed[subtype]) {
              property = {
                key,
                text: customEmbed[subtype].caption || key,
                isCustomEmbed: true,
              }
            }
          } else if (hashItems[key]) {
            property = hashItems[key]
            // Keep this condition for backward compatibility and convert to correct key
          } else if (customEmbed[key]) {
            property = {
              key: getKey(key),
              text: customEmbed[key].caption || key,
              isCustomEmbed: true,
            }
            compSettingArr[index] = getKey(key)
          }

          return {
            property,
            selected: true,
          }
        })
        .filter(({ property }) => !!property)
    } else {
      // If there is nothing, creat a placeholder array
      compSettingArr = []
    }

    // Loop over the defaults and add them to the bottom of the list
    defaults.forEach(function (item) {
      if (compSettingArr.indexOf(item.key) === -1) {
        formOptions.push({ property: item, selected: false })
      }
    })

    Object.entries(customEmbed).forEach(([subtype, embedData]) => {
      const customEmbedKey = getKey(subtype)
      if (!compSettingArr.includes(customEmbedKey)) {
        formOptions.push({
          property: {
            key: customEmbedKey,
            text: embedData.caption || customEmbedKey,
            isCustomEmbed: true,
          },
          selected: false,
        })
      }
    })

    // set the options to output to the user's form
    $scope[outputKey] = formOptions
    $scope.shouldRenderOptions = true
  }

  // function check (compSettingArr, defaults, outputKey) {
  //   if (!compSettingArr) {
  //     compSettingArr = []
  //   } else if (!$.isArray(compSettingArr)) {  // old version was not an array, but an object, so coverting over
  //     compSettingArr = Object.keys(compSettingArr)
  //   }
  //   let formOptions = []
  //       // Loop over the defaults and add them to the bottom of the list
  //   defaults.forEach(function (item) {
  //     const isSelected = compSettingArr.length === 0 || compSettingArr.indexOf(item.key) > -1
  //     formOptions.push({ property: item, selected: isSelected })
  //   })
  //       // set the options to output to the user's form
  //   $scope[outputKey] = formOptions
  // }

  this.updatePiklistOptions = (data) => {
    $scope.piklist = data
  }

  $scope.hasPikList = (key) =>
    $scope.piklist &&
    $scope.piklist.picklists &&
    $scope.piklist.picklists[key] !== undefined
  $scope.addPikList = (feature) => {
    window.open(
      `${CONSTANTS.TRIDENT_ROUTE_COMPOSER_UI}/picklist/${feature.key}`
    )
  }
  $scope.getPikListOpts = (key) => {
    // $scope.piklist && $scope.piklist[key]
    const list =
      $scope.piklist &&
      $scope.piklist.picklists &&
      $scope.piklist.picklists[key]
    if (list) {
      return list.picklist
        .filter((o) => o.active)
        .sort((a, b) => (a.order > b.order ? 1 : -1))
    }

    return []
  }

  $scope.sortableProps = {
    update: function () {
      // var elem = $(evt.target)
      // saveData(elem)
    },
    axis: 'y',
  }

  $scope.save = function () {
    const idleTime = _.get($scope.companySettings, 'locking.maxIdleTime')
    const warningTime = _.get($scope.companySettings, 'locking.warningTime')

    if ((idleTime || idleTime === 0) && idleTime < MIN_IDLE_TIMEOUT) {
      toastr.error(intl.get('settings.lock.maxIdle.validationMessage'))
    } else if (
      (warningTime || warningTime === 0) &&
      warningTime < MIN_IDLE_WARNING
    ) {
      toastr.error(intl.get('settings.lock.maxIdleWarning.validationMessage'))
    } else {
      $rootScope.$broadcast('BEFORE_SAVE_SETTINGS')
      const formData = Object.assign({}, $scope.companySettings)
      /* Convert the objects over into the simple key arrays */
      formData.tools = returnOnlyChecked($scope.toolbox_options)
      formData.v2 = formData.v2 || {}
      formData.v2.metas = $scope.metasFields.map(mapKeySettings)
      formData.v2.planning = $scope.planningFields.map(mapKeySettings)
      formData.edit = formData.edit || {}
      formData.pageBuilderPrvURL = sanitize(
        formData.pageBuilderPrvURL ?? ''
      ).replace(/(javascript:|data:|vbscript:)/gi, '')
      formData.labels = angular.copy(formData.labels)

      // mediaOption would lost reference from mediaOptions factory, using $rootScope instead
      formData.mediaOptions = _.pick(
        _.get($rootScope, 'globalSettingsModel.mediaOptions', {}),
        ['image', 'video', 'gallery']
      )

      settingsService
        .saveCompanySettings('org', formData)
        .then((res = {}) => {
          $scope.status = 'loaded'
          if (res.data) {
            $ngRedux.dispatch(setSettings({ data: res.data }))
          }
        })
        .catch((e) => {
          console.log('settings save err', e)
        })
    }
  }

  /* I can not get the ui-sortable to work. Will invesitagte later so doing this hacky method
    until I can have time to figure out why the binding are not working */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sortCheck = $interval(() => {
    $('[sortable]')
      .not('.added')
      .addClass('added')
      .each(function () {
        // https://api.jqueryui.com/sortable
        $(this).sortable({
          receive: function (evt, ui) {
            const ul = $(evt.target)
            const updatedIndex = ui.item.index()
            const opt = ui.item.data('opt')
            const model = angular.element(ul[0]).data('$ngModelController')
              .$modelValue
            const modelStr = ul.attr('ng-model')
            const key = opt.key

            if (modelStr !== 'metasFields') {
              $scope.metasFields = $scope.metasFields.filter(
                (o) => o.key !== key
              )
            }
            if (modelStr !== 'planningFields') {
              $scope.planningFields = $scope.planningFields.filter(
                (o) => o.key !== key
              )
            }
            if (modelStr !== 'meta_options_remaining') {
              $scope.meta_options_remaining = $scope.meta_options_remaining.filter(
                (o) => o.key !== key
              )
            }

            model.splice(updatedIndex, 0, opt)
            $(ui.item).remove()
          },
          update: function (evt, ui) {
            const li = $(evt.target)
            const updatedIndex = ui.item.index()
            const startIndex = li.data('startindex')

            if (startIndex !== updatedIndex && updatedIndex !== -1) {
              const model = angular.element(li[0]).data('$ngModelController')
                .$modelValue
              const data = model.splice(startIndex, 1)[0]
              model.splice(updatedIndex, 0, data)
            }
            li.removeData('startindex')
          },
          start: function (evt, ui) {
            const startIndex = ui.item.index()
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const elem = $(evt.target).data('startindex', startIndex)
          },
          connectWith: $(this).data('connectwith'),
          dropOnEmpty: true,
          cancel: '.unsortable',
          items: '> *:not(.unsortable)',
        })
      })

    $('[draggable][data-connectwith]').each(function () {
      $(this)
        .find('li')
        .not('.added')
        .addClass('added')
        .draggable({
          revert: true,
          connectToSortable: $(this).data('connectwith'),
        })
    })
  }, 100)

  $scope.advancedSettings = (obj, tab) => {
    if (tab === 'meta') {
      if (obj.settings && obj.settings.requiredSave) {
        delete obj.settings.requiredSave
      }
    }

    if (!obj.restrictions) {
      obj.restrictions = {}
    }

    if (obj.key === 'canonical_website') {
      obj.settings = {
        ...obj.settings,
        requiredSave: true,
        requiredPublish: true,
      }
    }

    $scope.activeFeature = obj
    $scope.activeFeatureTab = tab
    $scope.activeMetaDetails = metaItems[obj.key]
  }

  $scope.removeItem = (list, index) => {
    list.splice(index, 1)
    updateAvailableMetas(true)
  }

  $scope.clearItems = (list) => {
    // remove all the items from the array
    list.length = 0
    updateAvailableMetas(true)
  }

  const loadLabelsModel = () => {
    $scope.model = {
      labels: _labelsModel($scope),
    }
    $scope.status = 'loaded'
    $rootScope.settingsLoaded = true
  }

  settingsService
    .fetchCompanySettings('picklists')
    .then(({ data }) => this.updatePiklistOptions(data))
    .catch(({ status }) => {
      if (status === 404) {
        // no piklist generated yet
      } else {
        $scope.status = 'error'
      }
    })

  const fetchOrUpdatePromise = $rootScope.settingsLoaded
    ? this.updateSettingOptions($rootScope.globalSettings)
    : settingsService.fetchCompanySettings('org').then(({ data }) => {
        this.globalSettings = $rootScope.globalSettings = data
        // Add Default template
        storyTemplateService.list()
        return this.updateSettingOptions(data)
      })

  fetchOrUpdatePromise.then(loadLabelsModel).catch(() => {
    $scope.status = 'error'
  })

  $scope.updateSiteSelector = (websitesConfiguration) => {
    $scope.companySettings = {
      ...$scope.companySettings,
      siteServiceBasePathsV2: websitesConfiguration,
    }
  }

  // This method is used by settingsSyncEpic to retrofit settings object
  // updated by Redux to keep companySettings up to date
  $rootScope.$on('SETTINGS_RETROFIT', (_, data) => {
    $scope.companySettings = $rootScope.globalSettings = data.settings
    checkAndSort(
      $scope.companySettings.tools,
      $scope.companySettings.customEmbed,
      $scope.toolboxDefaults,
      'toolbox_options'
    )
  })

  $scope.deleteCustomEmbed = (subtype) => {
    $ngRedux.dispatch(deleteCustomEmbed(subtype))
  }

  $scope.deleteAnnotation = (annotation) => {
    $ngRedux.dispatch(deleteAnnotation(annotation))
  }

  $scope.openAnnotationModal = (data) => {
    renderAnnotationModal({
      visible: true,
      data,
    })
  }

  const renderAnnotationModal = ({ visible, data }) => {
    const toggle = () => {
      renderAnnotationModal({
        visible: false,
        data,
      })
    }

    $scope.renderCreateUpdateAnnotationModal &&
      $scope.renderCreateUpdateAnnotationModal({
        store: $ngRedux,
        visible,
        toggle,
        data,
      })
  }

  $scope.openTypographPluginModal = (checked) => {
    if (checked) {
      if ($scope.companySettings.convertQuotationsToFrechChevron) {
        return renderTypographPluginModal({
          visible: true,
        })
      }
      disableChevronAndAddTooltip()
    } else {
      const isSettingsDisabled = !$scope.companySettings
        .convertQuotationsToFrechChevron

      // This is executed when Typograph plugin is unchecked and Chevron is unchecked and enabled
      if (isSettingsDisabled) {
        enableChevronAndRemoveTooltip()
      }
    }
  }

  function disableChevronAndAddTooltip() {
    // Disable Chevron and uncheck it
    $scope.companySettings.convertQuotationsToFrechChevron = false
    $scope.companySettings.convertQuotationsToFrechChevronDisabled = true

    const oldWrapper = $('#convertQuotationsToFrechChevron').closest('span')
    const contents = oldWrapper.contents().detach()
    const newWrapper = $('<span></span>')

    newWrapper
      .attr('id', 'frechTooltipWrapper')
      .attr('data-toggle', 'tooltip')
      .attr('data-placement', 'bottom')
      .attr('data-trigger', 'hover')
      .attr('title', intl.get('settings.plugins.typograph.disable.tooltip'))

    newWrapper.append(contents)
    oldWrapper.replaceWith(newWrapper)
  }

  function enableChevronAndRemoveTooltip() {
    $scope.companySettings.convertQuotationsToFrechChevronDisabled = false

    const $wrapper = $('#convertQuotationsToFrechChevron').closest('span')
    $wrapper.tooltip('dispose')
    $wrapper.removeAttr('data-original-title aria-describedby')
    $wrapper.removeData('bs.tooltip')
  }

  const renderTypographPluginModal = ({ visible }) => {
    const toggle = () => {
      renderTypographPluginModal({
        visible: false,
      })
    }

    const handleCloseTypographPluginModal = () => {
      $scope.companySettings.disabledDraftPlugins.typograph = false
      toggle()
    }

    const handleDisableTypographClick = () => {
      disableChevronAndAddTooltip()
      toggle()
    }

    $scope.renderCreateUpdateTypographPluginModal &&
      $scope.renderCreateUpdateTypographPluginModal({
        store: $ngRedux,
        visible,
        toggle,
        handleDisableTypographClick,
        handleCloseTypographPluginModal,
      })
  }

  $scope.openContentElementSettingsModal = (type) => {
    renderContentElementSettingsModal({
      visible: true,
      type,
    })
  }

  const renderContentElementSettingsModal = ({ visible, type }) => {
    const toggle = () => {
      renderContentElementSettingsModal({
        visible: false,
        type,
      })
    }

    if (type === 'text') {
      $scope.renderTextSettingsModal &&
        $scope.renderTextSettingsModal({
          store: $ngRedux,
          visible,
          toggle,
        })
    } else {
      $scope.renderCustomEmbedSettingsModal &&
        $scope.renderCustomEmbedSettingsModal({
          store: $ngRedux,
          visible,
          toggle,
          subtype: type && getSubtype(type),
        })
    }
  }

  $scope.upsertLineConfiguration = (websiteLineConfig) => {
    const { website, ...lineConfig } = websiteLineConfig

    if (website === 'default') {
      $scope.companySettings = {
        ...$scope.companySettings,
        lineSettings: {
          ...$scope.companySettings.lineSettings,
          default: lineConfig,
        },
      }
    } else {
      $scope.companySettings = {
        ...$scope.companySettings,
        lineSettings: {
          ...$scope.companySettings.lineSettings,
          websites: {
            ...$scope.companySettings.lineSettings.websites,
            [website]: lineConfig,
          },
        },
      }
    }
  }

  $scope.deleteLineConfiguration = (websiteName) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [websiteName]: deletedConfig,
      ...remainingConfigs
    } = $scope.companySettings.lineSettings.websites

    $scope.companySettings = {
      ...$scope.companySettings,
      lineSettings: {
        ...$scope.companySettings.lineSettings,
        websites: remainingConfigs,
      },
    }
  }

  $scope.onChangeForSubtypes = (subtypeLists) => {
    $scope.companySettings = {
      ...$scope.companySettings,
      linkListSubtypes: subtypeLists.linkListSubtypes,
      linkSubtypes: subtypeLists.linkSubtypes,
    }
  }

  $scope.handleProofreaderSettingsChange = (proofreaderSettings) => {
    $scope.companySettings = {
      ...$scope.companySettings,
      proofreaders: proofreaderSettings,
    }
  }

  $scope.renderOpenBetaToggleElement = async () => {
    $scope.renderOpenBetaToggle &&
      $scope.renderOpenBetaToggle({
        visible: true,
        companySettings: $scope.companySettings,
        handleOpenBetaSettings: $scope.handleOpenBetaSettings,
      })
  }

  $scope.renderAutomationNotificationElement = async () => {
    $scope.renderAutomationNotification &&
      $scope.renderAutomationNotification({
        visible: true,
        companySettings: $scope.companySettings,
        handleAction: $scope.handleAutomationNotificationSettings,
      })
  }

  $scope.renderHyperlinkConfigElement = () => {
    $scope.renderHyperlinkConfig &&
      $scope.renderHyperlinkConfig({
        visible: true,
        companySettings: $scope.companySettings,
        handleHyperlinkConfigSettings: $scope.handleHyperlinkConfigSettings,
      })
  }

  $scope.getListLabelsKickers = (kickers) => {
    if (kickers) {
      return Object.keys(kickers)
    }
    return []
  }
}
